// Auth — the Google ID token the client attaches to every Worker call as
// `Authorization: Bearer <token>` (src/worker.js reads it exactly this way).
//
// Sign-out (a real gap in the current app, per this phase's brief): on a
// shared clinic desktop, someone hands the screen to the next person. Without
// this, the browser silently stays signed in as whoever used it last. Sign-
// out here does three things, all required:
//   1. clears the stored token,
//   2. clears the IndexedDB mirror (clearStore(), store.ts) — the next
//      person must not see the previous person's cached data even offline,
//   3. calls Google Identity Services' disableAutoSelect() so Google does not
//      silently re-sign the next person in as the last account on next load.
import { signal } from '@preact/signals';
import { clearStore } from './store';
import { resetViewAs } from './viewAs';
import { forgetDataVersions } from './dataVersions';

const TOKEN_KEY = 'nvmanager.idToken';

export const authToken = signal<string | null>(readStoredToken());

function readStoredToken(): string | null {
  try {
    return window.localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setToken(token: string): void {
  authToken.value = token;
  try {
    window.localStorage.setItem(TOKEN_KEY, token);
  } catch {
    // Storage can be unavailable (private mode, quota); the in-memory
    // signal still holds the token for this session.
  }
}

/** The Google Identity Services surface this module calls on sign-out.
 * Declared narrowly here rather than pulling in @types/google.accounts,
 * to keep Phase 1's dependency footprint small. */
interface GoogleIdShim {
  accounts?: {
    id?: {
      disableAutoSelect?: () => void;
    };
  };
}

function google(): GoogleIdShim | undefined {
  return (window as unknown as { google?: GoogleIdShim }).google;
}

/**
 * Sign out. Clears the token, clears the IndexedDB mirror, and tells Google
 * to stop auto-selecting this account on the next load. Safe to call even if
 * the Google Identity Services script never loaded (e.g. in tests) — that
 * branch is a no-op, not a thrown error.
 */
export async function signOut(): Promise<void> {
  authToken.value = null;
  try {
    window.localStorage.removeItem(TOKEN_KEY);
  } catch {
    // ignore — nothing left to clear
  }
  await clearStore();
  // The saved data versions name the signed-in person's email; the next
  // person at a shared desktop must not find it (Cursor review #59).
  forgetDataVersions();
  // The lens goes with the session. Handing the screen to the next person at a
  // shared clinic desktop must not hand them the last person's view-as.
  resetViewAs();
  const g = google();
  g?.accounts?.id?.disableAutoSelect?.();
}
